import PropTypes from "prop-types";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FieldDescription } from "@/components/ui/field";
import { FormInput } from "components/BaseForm";
import TestConnectionControl from "routes/swoogo/TestConnectionControl";

const STATUS_LABELS = {
  database: "Using stored credentials",
  environment:
    "Using environment credentials (backup); saving here replaces them",
  none: "Not configured",
};

const SWOOGO_API_CREDENTIALS_URL = "https://www.swoogo.com/manage/profile/api";
const SWOOGO_AUTH_DOCS_URL =
  "https://developer.swoogo.com/api-reference/authentication/request-api-token";
const LINK_CLASSES = "tw:text-[var(--color-blue)] tw:underline";

const AccountDetailsCard = ({ connection, testMutation }) => {
  const isStored = connection.source === "database";
  const keepPlaceholder = isStored
    ? "Leave blank to keep the stored value"
    : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>API Credentials</CardTitle>
        <CardDescription>
          {"These values come from Swoogo. Log in, open "}
          <a
            href={SWOOGO_API_CREDENTIALS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_CLASSES}
          >
            My Profile &gt; API Credentials
          </a>
          {
            ", and copy the consumer key and consumer secret. Pleteo asks Swoogo for an access token with them before saving, so rejected credentials are never stored. "
          }
          <a
            href={SWOOGO_AUTH_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_CLASSES}
          >
            Swoogo authentication documentation
          </a>
          .
        </CardDescription>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-5">
        <Badge
          variant="secondary"
          data-testid="swoogo-connection-status"
          className="tw:h-auto tw:whitespace-normal"
        >
          {STATUS_LABELS[connection.source]}
        </Badge>
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <FormInput name="account_name" label="Account Name" required />
          <FieldDescription>
            A label for this connection inside RVX-LC. It is not sent to Swoogo.
          </FieldDescription>
        </div>
        <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <FormInput
              name="api_key"
              label="Consumer Key"
              required={!isStored}
              placeholder={keepPlaceholder}
            />
            <FieldDescription>
              The public identifier of your Swoogo API credentials.
            </FieldDescription>
          </div>
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <FormInput
              name="api_secret"
              label="Consumer Secret"
              type="password"
              // Browsers treat a password field next to a text field as a
              // login form and fill it with the user's own credentials.
              autoComplete="new-password"
              required={!isStored}
              placeholder={keepPlaceholder}
            />
            <FieldDescription>
              Leave both fields blank to keep the stored credentials.
            </FieldDescription>
          </div>
        </div>
        <div className="tw:flex tw:items-center tw:gap-3">
          <TestConnectionControl
            isConfigured={connection.is_configured}
            testMutation={testMutation}
          />
        </div>
      </CardContent>
    </Card>
  );
};

AccountDetailsCard.propTypes = {
  connection: PropTypes.shape({
    source: PropTypes.string.isRequired,
    is_configured: PropTypes.bool,
  }).isRequired,
  testMutation: PropTypes.object.isRequired,
};

export default AccountDetailsCard;
